// shadcn-map registry API adapted to CRA + JavaScript/JSX.
// Source registry: @shadcn-map/map (MIT) — https://shadcn-map.vercel.app/docs
// shadcn-map itself is built on Leaflet + React Leaflet; those dependencies are intentional.
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "maplibre-gl/dist/maplibre-gl.css";
import "@maplibre/maplibre-gl-leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.fullscreen/dist/Control.FullScreen.css";
import "leaflet.fullscreen";
import { LoaderCircle, MapPin, Maximize, Minimize, Minus, Navigation, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { renderToString } from "react-dom/server";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { MapContainer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import { Button } from "./button";
import { cn } from "../../lib/utils";

export function Map({ center, zoom = 15, maxZoom = 18, className, children, ...props }) {
  return (
    <MapContainer
      center={center}
      zoom={zoom}
      maxZoom={maxZoom}
      minZoom={1}
      attributionControl={true}
      zoomControl={false}
      className={cn("z-0 size-full min-h-96 flex-1 rounded-md", className)}
      {...props}
    >
      {children}
    </MapContainer>
  );
}

export function MapTileLayer({
  url = process.env.REACT_APP_MAP_STYLE_URL || "https://tiles.openfreemap.org/styles/bright",
  attribution = '<a href="https://openfreemap.org/">OpenFreeMap</a> &copy; <a href="https://www.openmaptiles.org/">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
}) {
  const map = useMap();
  useEffect(() => {
    map.attributionControl?.setPrefix("");
    const layer = L.maplibreGL({
      style: url,
      // The adapter renders these credits in Leaflet's attribution control.
      attributionControl: { customAttribution: attribution },
    }).addTo(map);
    return () => { map.removeLayer(layer); };
  }, [map, url, attribution]);
  return null;
}

export function MapMarker({
  icon = <MapPin className="size-6 fill-background text-foreground drop-shadow" />,
  iconAnchor = [12, 12],
  popupAnchor = [0, -12],
  tooltipAnchor,
  className,
  ...props
}) {
  const leafletIcon = useMemo(() => L.divIcon({
    className: cn("shadcn-map-marker", className),
    html: renderToString(icon),
    iconAnchor,
    popupAnchor,
    tooltipAnchor,
  }), [icon, iconAnchor, popupAnchor, tooltipAnchor, className]);
  return <Marker icon={leafletIcon} {...props} />;
}

export function MapMarkerClusterGroup({ icon, children, ...props }) {
  const iconCreateFunction = icon
    ? (cluster) => L.divIcon({ className: "shadcn-map-cluster", html: renderToString(icon(cluster.getChildCount())) })
    : undefined;
  return (
    <MarkerClusterGroup
      chunkedLoading
      showCoverageOnHover={false}
      maxClusterRadius={48}
      iconCreateFunction={iconCreateFunction}
      {...props}
    >
      {children}
    </MarkerClusterGroup>
  );
}

export function MapPopup({ className, children, ...props }) {
  return (
    <Popup className={cn("shadcn-map-popup", className)} {...props}>
      {children}
    </Popup>
  );
}

export function MapControlContainer({ className, ...props }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !map.getContainer) return;
  }, [map]);
  return <div className={cn("absolute z-[1000]", className)} {...props} />;
}

export function MapZoomControl({ position = "left-3 top-3", className }) {
  const map = useMap();
  const [zoomLevel, setZoomLevel] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoomLevel(map.getZoom()) });
  return (
    <MapControlContainer className={cn(position, className)}>
      <div className="flex flex-col overflow-hidden rounded-[18px] bg-md-surface shadow-md2">
        <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-none border-0 border-b border-md-outline-variant text-md-on-surface hover:bg-md-secondary-container" disabled={zoomLevel >= map.getMaxZoom()} onClick={() => map.zoomIn()} aria-label="Aumenta zoom"><Plus className="h-4 w-4" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-none border-0 text-md-on-surface hover:bg-md-secondary-container" disabled={zoomLevel <= map.getMinZoom()} onClick={() => map.zoomOut()} aria-label="Riduci zoom"><Minus className="h-4 w-4" /></Button>
      </div>
    </MapControlContainer>
  );
}

export function MapFullscreenControl({ position = "right-3 top-3", className }) {
  const map = useMap();
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const enter = () => setIsFullscreen(true);
    const exit = () => setIsFullscreen(false);
    map.on("enterFullscreen", enter);
    map.on("exitFullscreen", exit);
    return () => { map.off("enterFullscreen", enter); map.off("exitFullscreen", exit); };
  }, [map]);
  const toggle = () => {
    if (typeof map.toggleFullscreen === "function") map.toggleFullscreen();
    else {
      const el = map.getContainer();
      if (!document.fullscreenElement) el.requestFullscreen?.(); else document.exitFullscreen?.();
    }
  };
  return (
    <MapControlContainer className={cn(position, className)}>
      <Button type="button" variant="ghost" size="icon" className="h-11 w-11 bg-md-surface text-md-on-surface shadow-md2 hover:bg-md-secondary-container" onClick={toggle} aria-label={isFullscreen ? "Esci da schermo intero" : "Schermo intero"}>
        {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
      </Button>
    </MapControlContainer>
  );
}

export function MapLocateControl({ watch = false, position = "right-3 bottom-10", className, onLocationFound, onLocationError }) {
  const map = useMap();
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const stop = () => {
    map.stopLocate();
    map.off("locationfound");
    map.off("locationerror");
    setLocation(null);
    setLoading(false);
  };
  const start = () => {
    setLoading(true);
    map.locate({ setView: true, maxZoom: Math.min(map.getMaxZoom(), 16), watch });
    map.once("locationfound", (event) => { setLocation(event.latlng); setLoading(false); onLocationFound?.(event); });
    map.once("locationerror", (error) => { setLocation(null); setLoading(false); onLocationError?.(error); });
  };
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <MapControlContainer className={cn(position, className)}>
        <Button type="button" variant="ghost" size="icon" className={`h-11 w-11 shadow-md2 ${location ? "bg-md-primary text-md-on-primary hover:bg-md-primary" : "bg-md-surface text-md-on-surface hover:bg-md-secondary-container"}`} onClick={location ? stop : start} disabled={loading} aria-label="Trova la mia posizione">
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
        </Button>
      </MapControlContainer>
      {location && <MapMarker position={location} icon={<span className="relative block h-4 w-4 rounded-full border-2 border-background bg-primary shadow"><span className="absolute inset-0 animate-ping rounded-full bg-primary opacity-40" /></span>} />}
    </>
  );
}

// Extension specifica dell'app: non fa parte del registry shadcn-map.
export function MapBoundsListener({ onChange }) {
  useMapEvents({ moveend: (event) => {
    if (!onChange) return;
    const b = event.target.getBounds();
    onChange([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
  }});
  return null;
}

export function MapFlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) map.flyTo(center, zoom || map.getZoom(), { duration: 0.5 });
  }, [map, center, zoom]);
  return null;
}
